import { DownloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useGetFileRequestInfoQuery,
  useLazyDownloadFileRequestQuery,
} from '@app/api/UserFileRequests';
import {
  FILE_EXPORT_LABEL,
  FRONT_WARNING,
  PREFIX,
  WARNING,
  WIDTHS_MOBILE_IPHONE_14_PRO,
  XLSX,
} from '@app/constants';
import { useContentWidth, useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { formatBytes, handleTimestamp } from '@app/utils';

import '@pages/Clients/ClientsExport/styles/ClientsExport.desktop.scss';

const { Title, Text } = Typography;

export const ClientsExport = () => {
  const [searchParams] = useSearchParams();
  const secretId = String(searchParams.get('fileId') || '');
  const { params, isAuth } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [downloadButtonLoader, setDownloadButtonLoader] = useState(false);
  const { contentWidth } = useContentWidth();
  const columnFilling = contentWidth > 652 ? 12 : 24;
  const downloadLinkRef = useRef<HTMLAnchorElement | null>(null);

  const {
    data,
    error: fileRequestInfoError,
    isFetching,
    refetch,
  } = useGetFileRequestInfoQuery(
    { ActiveBranchId: params.ActiveBranchId, secretId },
    { skip: !isAuth }
  );
  const [downloadFileRequest] = useLazyDownloadFileRequestQuery();

  useEffect(() => {
    if (fileRequestInfoError) {
      showNotification(handleCatchError(fileRequestInfoError), WARNING);
    }
  }, [fileRequestInfoError, showNotification]);

  const downloadFile = async () => {
    if (!params.ActiveBranchId) return;

    try {
      setDownloadButtonLoader(true);

      const { Name, Data } = await downloadFileRequest({
        ActiveBranchId: params.ActiveBranchId,
        secretId,
      }).unwrap();

      const link = downloadLinkRef.current;

      if (!link)
        return showNotification(
          'Ссылка на скачиваемый файл не найдена',
          FRONT_WARNING
        );

      link.download = Name || '';
      link.href = `data:${XLSX};base64,${Data || ''}`;
      link.click();

      refetch();

      // TODO: добавить обработчик неготового к загрузке файла
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setDownloadButtonLoader(false);
    }
  };

  if (isFetching || !data) {
    return (
      <Skeleton
        active
        style={{ maxWidth: contentWidth > 652 ? '600px' : '100%' }}
      />
    );
  }

  return (
    <div className={`${PREFIX}clients-export`}>
      <Title level={3} data-testid="export-file-block-title">
        {FILE_EXPORT_LABEL}
      </Title>

      <Space className="content-margins" direction="vertical" size="middle">
        <Card>
          <div
            className="meta-table"
            style={{
              minWidth: contentWidth > 652 ? '600px' : `${contentWidth - 20}px`,
            }}
          >
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="data-title">
                  Данные:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="file-name">
                {data.Subject || '-'}
              </Col>
            </Row>
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="branch-title">
                  Филиал:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="branch-name">
                {data.BranchName || '-'}
              </Col>
            </Row>
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="creation-time-title">
                  Время создания:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="creation-time">
                {handleTimestamp(data.CreateTime)}
              </Col>
            </Row>
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="created-owner-title">
                  Пользователь:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="created-owner">
                {data.UserName || '-'}
              </Col>
            </Row>
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="download-count-title">
                  Количество скачиваний:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="download-count">
                {data.DownloadCount}
              </Col>
            </Row>
            <Row wrap>
              <Col span={columnFilling}>
                <Text strong data-testid="file-name-title">
                  Имя файла:
                </Text>
              </Col>
              <Col span={columnFilling} data-testid="file-name-with-date">
                {data.FileName ? `${data.FileName}.xlsx` : '-'}
              </Col>
            </Row>
          </div>
        </Card>

        {data.CanDownload ? (
          <>
            <Button
              type="primary"
              loading={downloadButtonLoader}
              icon={<DownloadOutlined />}
              block={contentWidth > WIDTHS_MOBILE_IPHONE_14_PRO ? false : true}
              onClick={downloadFile}
              data-testid="download-clients-button"
            >
              Скачать .xlsx файл ({formatBytes(data.FileSize)})
            </Button>

            <Alert
              message={`Файл доступен к скачиванию до ${handleTimestamp(
                data.FileLifetime
              )}`}
              type="warning"
              showIcon
              data-testid="download-link-hint"
            />
          </>
        ) : (
          <Alert
            message="Время скачивания файла истекло!"
            type="error"
            showIcon
            data-testid="download-link-hint"
          />
        )}
      </Space>

      <a ref={downloadLinkRef} className="clients-downloading-button" />
    </div>
  );
};
